import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { extractIdFromSlug } from '../utils/slugUtils';
import { 
  QuestionMarkCircleIcon, 
  AcademicCapIcon, 
  CalendarIcon,
  TagIcon,
  UserIcon,
  ArrowLeftIcon,
  PlayIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import publicQuizService from '../services/publicQuizService';
import copyService from '../services/copyService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const PublicQuizDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  
  const [quiz, setQuiz] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    const fetchQuizData = async () => {
      if (!slug) return;
      
      const id = extractIdFromSlug(slug);
      
      setLoading(true);
      setError(null);
      
      try {
        const quizData = await publicQuizService.getQuizById(id);
        setQuiz(quizData);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load quiz details');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizData();
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleStartQuiz = async () => {
    if (user && slug) {
      const id = extractIdFromSlug(slug);
      setCopying(true);
      try {
        const result = await copyService.copyQuiz(id);
        toast.success(result.message);
        navigate('/quizzes');
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Erro ao copiar quiz');
      } finally {
        setCopying(false);
      }
    } else {
      navigate(`/register?redirect=/quizzes/management/${slug}`);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${
        isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <QuestionMarkCircleIcon className="h-16 w-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Quiz não encontrado</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {error || 'O quiz que você está procurando não existe ou foi removido.'}
        </p>
        <Link
          to="/search"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Voltar para pesquisa
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* Enhanced Title & Description */}
        <title>{`${quiz.title || 'Quiz'} - ${quiz.questions?.length || 0} Questões | Quiz Interativo Grátis | Upmindy`}</title>
        <meta name="description" content={`${quiz.description || `Quiz interativo com ${quiz.questions?.length || 0} questões`} | Quiz online grátis no Upmindy.`} />
        
        {/* Keywords & SEO */}
        <meta name="keywords" content={quiz.tags?.join(', ') || 'quiz, educação, aprendizado, perguntas, teste'} />
        <meta name="author" content={quiz.creatorName || 'Upmindy'} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta httpEquiv="Content-Language" content="pt-BR" />
        <meta name="theme-color" content="#3B82F6" />
        
        {/* Canonical URLs */}
        <link rel="canonical" href={`https://upmindy.com/quizzes/${slug}`} />
        <link rel="alternate" hrefLang="pt-BR" href={`https://upmindy.com/quizzes/${slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://upmindy.com/quizzes/${slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={`${quiz.title || 'Quiz'} - Quiz Interativo | Upmindy`} />
        <meta property="og:description" content={quiz.description || `${quiz.questions?.length || 0} questões para testar seus conhecimentos`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://upmindy.com/quizzes/${slug}`} />
        <meta property="og:image" content="https://upmindy.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Upmindy" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="article:published_time" content={quiz.createdAt} />
        <meta property="article:modified_time" content={quiz.updatedAt} />
        <meta property="article:author" content={quiz.creatorName || 'Upmindy'} />
        <meta property="article:section" content="Education" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@upmindy_app" />
        <meta name="twitter:creator" content="@upmindy_app" />
        <meta name="twitter:title" content={`${quiz.title || 'Quiz'} - Quiz | Upmindy`} />
        <meta name="twitter:description" content={quiz.description || `${quiz.questions?.length || 0} questões para testar seus conhecimentos`} />
        <meta name="twitter:image" content="https://upmindy.com/og-image.png" />
        <meta name="twitter:image:alt" content={`Quiz: ${quiz.title || 'Upmindy'}`} />
        <meta name="twitter:label1" content="Questões" />
        <meta name="twitter:data1" content={`${quiz.questions?.length || 0} perguntas`} />
        <meta name="twitter:label2" content="Tipo" />
        <meta name="twitter:data2" content="Quiz Interativo" />
        
        {/* Structured Data - Quiz/LearningResource Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["Quiz", "LearningResource"],
            "name": quiz.title || "Quiz Interativo",
            "description": quiz.description || `Quiz interativo com ${quiz.questions?.length || 0} questões`,
            "educationalLevel": "intermediate",
            "inLanguage": "pt-BR",
            "learningResourceType": "Quiz",
            "isAccessibleForFree": true,
            "timeRequired": `PT${Math.ceil((quiz.questions?.length || 0) * 2)}M`,
            "creator": {
              "@type": "Person",
              "name": quiz.creatorName || "Upmindy"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Upmindy",
              "url": "https://upmindy.com"
            },
            "datePublished": quiz.createdAt,
            "dateModified": quiz.updatedAt,
            "url": `https://upmindy.com/quizzes/${slug}`,
            "keywords": quiz.tags?.join(', ') || 'quiz, educação'
          })}
        </script>
        
        {/* Structured Data - Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Início",
                "item": "https://upmindy.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Explorar",
                "item": "https://upmindy.com/search"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": quiz.title || "Quiz",
                "item": `https://upmindy.com/quizzes/${slug}`
              }
            ]
          })}
        </script>
        
        {/* Structured Data - FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `Quantas questões tem o quiz ${quiz.title || 'este'}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `Este quiz contém ${quiz.questions?.length || 0} questões cuidadosamente elaboradas.`
                }
              },
              {
                "@type": "Question",
                "name": "Posso fazer este quiz gratuitamente?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sim! Este quiz está disponível gratuitamente no Upmindy."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Breadcrumbs */}
            <nav className="flex mb-6" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <Link
                    to="/"
                    className={`inline-flex items-center text-sm font-medium ${
                      isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Início
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <Link
                      to="/search"
                      className={`ml-1 text-sm font-medium md:ml-2 ${
                        isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Explorar
                    </Link>
                  </div>
                </li>
                <li aria-current="page">
                  <div className="flex items-center">
                    <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className={`ml-1 text-sm font-medium md:ml-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      {quiz.title.length > 30 ? `${quiz.title.substring(0, 30)}...` : quiz.title}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>

            <div className="flex items-start space-x-4">
              <QuestionMarkCircleIcon className="h-12 w-12 sm:h-16 sm:w-16 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">{quiz.title}</h1>
                {quiz.description && (
                  <p className={`text-base sm:text-lg mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {quiz.description}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center">
                    <QuestionMarkCircleIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <span>{quiz.questions?.length || 0} perguntas</span>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <span>{formatDate(quiz.createdAt)}</span>
                  </div>
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <span>Criado por {quiz.creatorName || 'Upmindy'}</span>
                  </div>
                </div>

                {quiz.tags && quiz.tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {quiz.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                          isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleStartQuiz}
                    disabled={copying}
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {copying ? (
                      'Copiando...'
                    ) : (
                      <>
                        <PlayIcon className="h-5 w-5 mr-2" />
                        {user ? 'Copiar e Iniciar Quiz' : 'Começar Quiz'}
                      </>
                    )}
                  </button>
                  
                  <Link
                    to="/search"
                    className={`flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg border transition-colors font-medium ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Explorar Mais
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Questions Preview */}
        {quiz.questions && quiz.questions.length > 0 && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-xl font-bold mb-4">Perguntas do Quiz</h2>
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm mb-4`}>
              Este quiz contém {quiz.questions.length} {quiz.questions.length === 1 ? 'pergunta' : 'perguntas'}. 
              {user ? ' Copie para sua coleção para começar!' : ' Faça login para começar!'}
            </div>
            <div className="grid gap-4">
              {quiz.questions.slice(0, 3).map((q: any, index: number) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="font-medium mb-2">
                    {index + 1}. {q.question}
                  </div>
                  <div className="text-sm opacity-75">
                    {(q.wrongAnswers?.length || 0) + 1} opções de resposta
                  </div>
                </div>
              ))}
              {quiz.questions.length > 3 && (
                <div className={`text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  +{quiz.questions.length - 3} perguntas adicionais
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PublicQuizDetail;
